import Image from "next/image"
import { ReactNode, useMemo } from "react"
import { Heading1, Heading2, Heading3 } from "../text"
import Section from "./Section"
type GenericSectionImage = {
  url: string
  alt: string
  side?: "left" | "right"
  height?: number
  width?: number
  backgroundColor?: string
  breakBounds?: boolean
  priority?: boolean
}

type GenericSectionProps = {
  header?: {
    text?: string
    type?: "h1" | "h2" | "h3"
  }
  image: GenericSectionImage
  content?: ReactNode
  button?: ReactNode
  headerSubtitle?: ReactNode
}

const GenericSection: React.FC<GenericSectionProps> = (props) => {
  const { header, content, image, button, headerSubtitle: HeaderSubtitle } = props
  const HeaderTag = useMemo(() => {
    switch (header?.type) {
      case "h1":
        return Heading1
      case "h2":
        return Heading2
      case "h3":
        return Heading3
      default:
        return Heading2
    }
  }, [header?.type])

  return (
    <Section className="mx-auto max-w-2xl md:max-w-4xl lg:max-w-7xl text-left">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div
          className={`
          order-1 
          ${image.side === "right" ? "lg:order-1" : "lg:order-2"}
        `}>
          {HeaderSubtitle}
          {!!header?.text && <HeaderTag className="mb-[1.5rem]">{header.text}</HeaderTag>}
          {content}
          {button}
        </div>
        <div
          className={`
          order-2 
          ${image.side === "right" ? "lg:order-2" : "lg:order-1"}
        `}>
          <div className="relative aspect-[4/3] w-full">
            {!image.breakBounds ? <GenericSectionImage image={image} /> : <GenericSectionImageOverflow image={image} />}
          </div>
        </div>
      </div>
    </Section>
  )
}

const GenericSectionImage: React.FC<{ image: GenericSectionImage }> = ({ image }) => (
  <div className="relative aspect-[4/3] w-full">
    {image && (
      <Image
        src={image.url}
        height={image.height ?? 640}
        width={image.width ?? 616}
        alt={image.alt}
        className="rounded-xl object-cover h-[640px] w-[616px]"
        priority={image.priority}
        sizes="(max-width: 1024px) 100vw, 616px"
      />
    )}
  </div>
)

const GenericSectionImageOverflow: React.FC<{ image: GenericSectionImage }> = ({ image }) => {
  const { url, alt, backgroundColor = "#FE664E", height, width, priority } = image
  return (
    <div className={`relative aspect-[4/3] w-full bg-[${backgroundColor}] rounded-xl h-[640px]`}>
      {url && (
        <Image
          src={url}
          height={height ?? 640}
          width={width ?? 616}
          alt={alt}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          priority={priority}
        />
      )}
    </div>
  )
}

export default GenericSection
